import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { mapBeneficialOwnerPartyDtoFormData } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/utils";
import { BeneficialOwnerPartyFormFields } from "Components/Users/UserDetailPageContent/Components/BeneficialOwnerPartyFormFields";
import { RelatedPartyDocumentsReupload } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyDocumentsReupload";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import {
  type BeneficialOwnerFormData,
  type BeneficialOwnerFormDataInput,
  createBeneficialOwnerValidationSchema,
} from "Components/Users/schemas";
import { type PartyDto, VatStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { EMPTY_PHONE } from "Utils/PhoneUtils";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form;

type Props = {
  partyData: PartyDto;
  onSaveSuccess?: () => void;
};

export const BeneficialOwnerPartyForm = ({
  partyData,
  onSaveSuccess,
}: Props) => {
  const { t } = useResource();
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();
  const schema = createBeneficialOwnerValidationSchema(t);

  const formMethods = useForm<
    BeneficialOwnerFormDataInput,
    unknown,
    BeneficialOwnerFormData
  >({
    resolver: zodResolver(schema),
    defaultValues: mapBeneficialOwnerPartyDtoFormData(partyData),
  });
  const {
    control,
    formState: { isDirty },
    reset,
    handleSubmit,
  } = formMethods;

  const { mutate: updateParty, isPending } = usePartyMutation({
    onSuccess: ({ data }: { data: PartyDto }) => {
      reset(mapBeneficialOwnerPartyDtoFormData(data));
      toast.success(t(COMPONENT_RESOURCES.Controls.SubmitSuccess));
      onSaveSuccess?.();
    },
    onError: () => toast.error(t(COMPONENT_RESOURCES.Controls.SubmitError)),
  });

  const onSubmit = (data: BeneficialOwnerFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    const payload: PartyDto = {
      ...data,
      isForeigner: false,
      licenses: [],
      relatedParties: [],
      // Required by the DTO shape only — the server ignores them on updates.
      verifiedPhone: EMPTY_PHONE,
      vatStatus: VatStatus.Unknown,
    };

    updateParty({
      publicID: data.publicID || "",
      data: payload,
    });
  };

  const { addresses, hasIdentificationsUploaded, publicID } = partyData;

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <BeneficialOwnerPartyFormFields
            control={control}
            addresses={addresses}
            partyPublicID={publicID ?? undefined}
          />
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={!isDirty}
            isLoading={isPending}
            variant="contained"
            color="primary"
          >
            {t(COMPONENT_RESOURCES.Controls.Save)}
          </PrimaryButton>
        </StyledForm>
      </AddressUncommittedProvider>
      {hasIdentificationsUploaded && publicID && (
        <Box mt={5}>
          <RelatedPartyDocumentsReupload partyData={partyData} />
        </Box>
      )}
    </FormProvider>
  );
};
